import { useState, useEffect, useCallback, useContext } from 'react';
import {
    Box, Typography, Button, Stack, Chip, CircularProgress, Textarea,
    Modal, ModalDialog, ModalClose, Checkbox,
} from '@mui/joy';
import { motion } from 'framer-motion';

import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import CancelRoundedIcon      from '@mui/icons-material/CancelRounded';
import AccessTimeRoundedIcon   from '@mui/icons-material/AccessTimeRounded';
import ArrowBackRoundedIcon    from '@mui/icons-material/ArrowBackRounded';

import Header from '../../components/Header';
import { AppContext } from '../../context';
import { APIGetAllFillRequests, APIReviewFillRequest, APIReviewFillRequestsBatch } from '../../api';
import { useNavigate } from 'react-router';
import MemeLoader from '../../components/MemeLoader';

const STATUS_MAP = {
    pending:  { label: 'Ожидает',   color: '#f59e0b', bg: 'rgba(245,158,11,0.12)' },
    approved: { label: 'Одобрена',  color: '#22c55e', bg: 'rgba(34,197,94,0.12)' },
    rejected: { label: 'Отклонена', color: '#f87171', bg: 'rgba(248,113,113,0.12)' },
};

const pageVariants = {
    initial: { opacity: 0, y: 16 },
    animate: { opacity: 1, y: 0, transition: { duration: 0.38 } },
    exit:    { opacity: 0, y: -12, transition: { duration: 0.22 } },
};

export default function FillRequestsPage() {
    const { token, showToast, onRefreshTick, hasPerm } = useContext(AppContext);
    const navigate = useNavigate();

    const canReview = hasPerm('schedule.fill_requests.review');

    const [requests, setRequests] = useState([]);
    const [loading,  setLoading]  = useState(true);
    const [selectedIds,  setSelectedIds]  = useState([]);
    const [batchLoading, setBatchLoading] = useState(false);
    const [reviewId, setReviewId] = useState(null);
    const [reviewAction, setReviewAction] = useState('');
    const [reviewComment, setReviewComment] = useState('');
    const [reviewing, setReviewing] = useState(false);

    const loadRequests = useCallback(async () => {
        setLoading(true);
        const data = await APIGetAllFillRequests(token);
        if (Array.isArray(data)) setRequests(data);
        setLoading(false);
    }, [token]);

    useEffect(() => { loadRequests(); }, [loadRequests]);

    useEffect(() => onRefreshTick(loadRequests), [onRefreshTick, loadRequests]);

    const handleReview = async () => {
        setReviewing(true);
        const res = await APIReviewFillRequest(token, reviewId, {
            action:  reviewAction,
            comment: reviewComment,
        });
        if (!res?.error) {
            showToast(reviewAction === 'approve' ? 'Заявка одобрена ✓' : 'Заявка отклонена');
            setReviewId(null);
            setReviewComment('');
            await loadRequests();
        } else {
            showToast('Ошибка', 'danger');
        }
        setReviewing(false);
    };

    const toggleSelect = (id) => {
        setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
    };

    const handleBatch = async (action) => {
        if (selectedIds.length === 0) return;
        setBatchLoading(true);
        const res = await APIReviewFillRequestsBatch(token, selectedIds, action);
        setBatchLoading(false);
        if (!res?.error) {
            const label = action === 'approve' ? 'одобрено' : 'отклонено';
            showToast(`Массово ${label}: ${res.count ?? selectedIds.length} ✓`, action === 'approve' ? 'success' : 'warning');
            setRequests(prev => prev.filter(r => !selectedIds.includes(r.id)));
            setSelectedIds([]);
        } else {
            showToast('Ошибка массового рассмотрения', 'danger');
        }
    };

    return (
        <motion.div variants={pageVariants} initial="initial" animate="animate" exit="exit"
            style={{ minHeight: '100vh', background: 'var(--joy-palette-background-body)' }}>
            <Header />
            <Box sx={{ maxWidth: 700, mx: 'auto', px: { xs: 2, sm: 3 }, py: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
                    <Box
                        onClick={() => navigate('/manager')}
                        sx={{
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            width: 36, height: 36,
                            borderRadius: 'var(--joy-radius-md)',
                            border: '1px solid var(--joy-palette-divider)',
                            bgcolor: 'var(--joy-palette-background-level1)',
                            cursor: 'pointer', transition: 'all .15s',
                            '&:hover': { borderColor: 'var(--joy-palette-primary-500)', bgcolor: 'rgba(162,38,226,0.08)' },
                        }}
                    >
                        <ArrowBackRoundedIcon sx={{ fontSize: 18, color: 'var(--joy-palette-text-secondary)' }} />
                    </Box>
                    <Typography sx={{ fontSize: 20, fontWeight: 800, lineHeight: 1.1, color: 'var(--joy-palette-text-primary)' }}>
                        Заявки на доступ к заполнению
                    </Typography>
                </Box>

                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                        <MemeLoader />
                    </Box>
                ) : requests.length === 0 ? (
                    <Typography sx={{ color: 'var(--joy-palette-text-tertiary)', textAlign: 'center', py: 6 }}>
                        Нет заявок
                    </Typography>
                ) : (
                    <>
                        {canReview && selectedIds.length > 0 && (
                            <Box sx={{
                                display: 'flex', alignItems: 'center', gap: 1, mb: 1.5,
                                p: 1, borderRadius: 'var(--joy-radius-lg)',
                                bgcolor: 'var(--joy-palette-background-level1)',
                                border: '1px solid var(--joy-palette-divider)',
                            }}>
                                <Typography sx={{ fontSize: 13, fontWeight: 700, flex: 1 }}>
                                    Выбрано: {selectedIds.length}
                                </Typography>
                                <Button size="sm" color="success" variant="soft" loading={batchLoading}
                                    onClick={() => handleBatch('approve')}>
                                    Одобрить
                                </Button>
                                <Button size="sm" color="danger" variant="soft" loading={batchLoading}
                                    onClick={() => handleBatch('reject')}>
                                    Отклонить
                                </Button>
                                <Button size="sm" variant="plain" color="neutral"
                                    onClick={() => setSelectedIds([])}>
                                    Сброс
                                </Button>
                            </Box>
                        )}
                        <Stack spacing={1.5}>
                            {requests.map((req) => {
                                const st = STATUS_MAP[req.status] || STATUS_MAP.pending;
                                return (
                                    <Box key={req.id} sx={{
                                        p: 2, borderRadius: 'var(--joy-radius-lg)',
                                        border: '1px solid var(--joy-palette-divider)',
                                        bgcolor: 'var(--joy-palette-background-level1)',
                                    }}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                                                {canReview && req.status === 'pending' && (
                                                    <Checkbox
                                                        checked={selectedIds.includes(req.id)}
                                                        onChange={() => toggleSelect(req.id)}
                                                        sx={{ mt: 0.3 }}
                                                    />
                                                )}
                                                <Box>
                                                    <Typography sx={{ fontSize: 14, fontWeight: 700, color: 'var(--joy-palette-text-primary)' }}>
                                                        {req.account_name}
                                                    </Typography>
                                                    <Typography sx={{ fontSize: 11, color: 'var(--joy-palette-text-tertiary)' }}>
                                                        Неделя с {req.week_start}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                            <Chip size="sm" sx={{ bgcolor: st.bg, color: st.color, fontWeight: 600, fontSize: 11 }}>
                                                {st.label}
                                            </Chip>
                                        </Box>

                                        <Typography sx={{ fontSize: 12, color: 'var(--joy-palette-text-secondary)', mb: 0.5 }}>
                                            <b>Причина:</b> {req.reason || '—'}
                                        </Typography>
                                        <Typography sx={{ fontSize: 12, color: 'var(--joy-palette-text-secondary)', mb: 0.5 }}>
                                            <b>PUSH получил:</b> {req.received_push ? 'Да' : 'Нет'}
                                        </Typography>
                                        {req.extra_comment && (
                                            <Typography sx={{ fontSize: 12, color: 'var(--joy-palette-text-tertiary)', mb: 0.5 }}>
                                                <b>Комментарий:</b> {req.extra_comment}
                                            </Typography>
                                        )}
                                        {req.review_comment && (
                                            <Typography sx={{ fontSize: 12, color: 'var(--joy-palette-text-tertiary)', mb: 0.5 }}>
                                                <b>Ответ руководителя:</b> {req.review_comment}
                                            </Typography>
                                        )}

                                        <Typography sx={{ fontSize: 10, color: 'var(--joy-palette-text-tertiary)', mt: 0.5 }}>
                                            Создана: {new Date(req.date_create).toLocaleString('ru-RU')}
                                        </Typography>

                                        {req.status === 'pending' && canReview && (
                                            <Box sx={{ display: 'flex', gap: 1, mt: 1.5 }}>
                                                <Button size="sm" color="success"
                                                    startDecorator={<CheckCircleRoundedIcon sx={{ fontSize: 14 }} />}
                                                    onClick={() => { setReviewId(req.id); setReviewAction('approve'); }}
                                                    sx={{ fontWeight: 700, fontSize: 12 }}>
                                                    Одобрить
                                                </Button>
                                                <Button size="sm" color="danger" variant="outlined"
                                                    startDecorator={<CancelRoundedIcon sx={{ fontSize: 14 }} />}
                                                    onClick={() => { setReviewId(req.id); setReviewAction('reject'); }}
                                                    sx={{ fontWeight: 700, fontSize: 12 }}>
                                                    Отклонить
                                                </Button>
                                            </Box>
                                        )}

                                        {req.status === 'approved' && req.is_fill_window_open && (
                                            <Chip size="sm" sx={{
                                                mt: 1, bgcolor: 'rgba(34,197,94,0.12)', color: '#22c55e',
                                                fontWeight: 600, fontSize: 11,
                                            }}>
                                                <AccessTimeRoundedIcon sx={{ fontSize: 12, mr: 0.3 }} />
                                                Окно заполнения ещё открыто
                                            </Chip>
                                        )}
                                    </Box>
                                );
                            })}
                        </Stack>
                    </>
                )}
            </Box>

            {/* Модалка подтверждения */}
            <Modal
                open={!!reviewId}
                onClose={() => setReviewId(null)}
                slotProps={{
                    backdrop: {
                        sx: {
                            backdropFilter: 'blur(8px)',
                            bgcolor: 'rgba(0,0,0,0.55)',
                            WebkitBackdropFilter: 'blur(8px)',
                        },
                    },
                }}
                sx={{ touchAction: 'none', overscrollBehavior: 'contain' }}
            >
                <ModalDialog sx={{
                    maxWidth: "90vw", width: '100%', bgcolor: 'var(--joy-palette-background-surface)',
                    border: '1px solid var(--joy-palette-divider)', borderRadius: 'var(--joy-radius-xl)',
                }}>
                    <ModalClose />
                    <Typography sx={{ fontSize: 16, fontWeight: 800, color: 'var(--joy-palette-text-primary)', mb: 1 }}>
                        {reviewAction === 'approve' ? 'Одобрить заявку?' : 'Отклонить заявку?'}
                    </Typography>
                    <Typography sx={{ fontSize: 12, color: 'var(--joy-palette-text-tertiary)', mb: 2 }}>
                        {reviewAction === 'approve'
                            ? 'Фотографу будет дан 1 час на заполнение графика.'
                            : 'Фотограф не получит доступ к заполнению.'}
                    </Typography>
                    <Textarea
                        minRows={2}
                        value={reviewComment}
                        onChange={(e) => setReviewComment(e.target.value)}
                        placeholder="Комментарий (необязательно)..."
                        sx={{ mb: 2, color: 'white', bgcolor: 'var(--joy-palette-background-level1)' }}
                    />
                    <Button
                        onClick={handleReview}
                        loading={reviewing}
                        color={reviewAction === 'approve' ? 'success' : 'danger'}
                        sx={{ fontWeight: 700 }}
                    >
                        {reviewAction === 'approve' ? 'Одобрить' : 'Отклонить'}
                    </Button>
                </ModalDialog>
            </Modal>
        </motion.div>
    );
}